import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardHeader, CardContent, Heading, Text, Button, Flex, Plus, LoadingOrbit, TextField, TagBadge, useToast } from '@chatsdet/ui';
import { GithubLogo, Trash, LinkSimple, GitBranch, PencilSimple } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { useAuth } from '../../../contexts/AuthContext';
import { PropertyCodeEditRepositoryDialog } from './PropertyCodeEditRepositoryDialog';
import { PropertyCodeLinkRepositoryDialog } from './PropertyCodeLinkRepositoryDialog';
import { PropertyCodeRunbooksSection } from './PropertyCodeRunbooksSection';
import { PropertyCodeMonitorsSection } from './PropertyCodeMonitorsSection';
import type { GitHubConnection, GitHubRepository, LinkedRepo } from './PropertyCodeTab.types';

interface PropertyCodeTabProps {
  propertyId: string;
}

export function PropertyCodeTab({ propertyId }: PropertyCodeTabProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const releaseQaGithubChecksEnabled = !!user?.features?.releaseQaGithubChecks;
  const prReviewEnabled = !!user?.features?.prReview;
  const [isLinkDialogOpen, setIsLinkDialogOpen] = React.useState(false);
  const [selectedRepo, setSelectedRepo] = React.useState<string | null>(null);
  const [linkRootDir, setLinkRootDir] = React.useState('/');
  const [linkAgentHint, setLinkAgentHint] = React.useState('');

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editingRepo, setEditingRepo] = React.useState<LinkedRepo | null>(null);
  const [editRootDir, setEditRootDir] = React.useState('/');
  const [editAgentHint, setEditAgentHint] = React.useState('');
  const [editPrReviewGuidance, setEditPrReviewGuidance] = React.useState('');

  // Release-check glob is edited per-repo inline in its own section. Track
  // dirty values keyed by repoId; the source of truth is the list query.
  const [globDrafts, setGlobDrafts] = React.useState<Record<string, string>>({});

  // Check GitHub connection status
  const githubQuery = useQuery({
    queryKey: ['github-status'],
    queryFn: async (): Promise<GitHubConnection> => {
      const res = await fetch(`${API_URL}/integrations/github/status`, {
        credentials: 'include',
      });
      return res.json();
    },
  });

  // Fetch linked repos for this property
  const linkedReposQuery = useQuery({
    queryKey: ['property-github-repos', propertyId],
    queryFn: async (): Promise<{ ok: boolean; items: LinkedRepo[] }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/github-repos`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch linked repos');
      return res.json();
    },
  });

  // Fetch available repos from GitHub (only when connected)
  const availableReposQuery = useQuery({
    queryKey: ['github-repositories'],
    queryFn: async (): Promise<{ ok: boolean; repositories?: GitHubRepository[] }> => {
      const res = await fetch(`${API_URL}/integrations/github/repositories`, {
        credentials: 'include',
      });
      return res.json();
    },
    enabled: githubQuery.data?.ok === true,
  });

  // Link repo mutation
  const linkRepoMutation = useMutation({
    mutationFn: async ({
      repoFullName,
      rootDir,
      agentHint,
    }: {
      repoFullName: string;
      rootDir?: string;
      agentHint?: string;
    }) => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/github-repos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ repoFullName, rootDir, agentHint: agentHint || undefined }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to link repository');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-github-repos', propertyId] });
      setIsLinkDialogOpen(false);
      setSelectedRepo(null);
      setLinkRootDir('/');
      setLinkAgentHint('');
      toast.success('Repository linked successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to link repository');
    },
  });

  // Update repo mutation
  const updateRepoMutation = useMutation({
    mutationFn: async ({
      repoId,
      rootDir,
      agentHint,
      releaseCheckBranchGlob,
      prReviewGuidance,
    }: {
      repoId: string;
      rootDir?: string;
      agentHint?: string | null;
      releaseCheckBranchGlob?: string | null;
      prReviewGuidance?: string | null;
    }) => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/github-repos/${repoId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ rootDir, agentHint, releaseCheckBranchGlob, prReviewGuidance }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to update repository');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-github-repos', propertyId] });
      setIsEditDialogOpen(false);
      setEditingRepo(null);
      toast.success('Repository settings updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update repository');
    },
  });

  // Unlink repo mutation
  const unlinkRepoMutation = useMutation({
    mutationFn: async (repoFullName: string) => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/github-repos/${encodeURIComponent(repoFullName)}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error('Failed to unlink repository');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-github-repos', propertyId] });
      toast.success('Repository unlinked');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to unlink repository');
    },
  });

  // Filter out already linked repos from available repos
  const linkedFullNames = new Set(linkedReposQuery.data?.items?.map((r) => r.repoFullName) || []);
  const unlinkedRepos =
    availableReposQuery.data?.repositories?.filter((r) => !linkedFullNames.has(r.fullName)) || [];

  // Loading state
  if (githubQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  // GitHub not connected state
  if (!githubQuery.data?.ok) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-8">
        <div className="w-full max-w-xl text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-foreground/5">
            <GithubLogo size={24} className="text-foreground" weight="duotone" />
          </div>
          <Text size="lg" weight="semibold" className="text-foreground mb-1">
            Connect GitHub to link repositories
          </Text>
          <Text size="sm" className="text-muted-foreground max-w-sm mx-auto">
            Link your code repositories to this application to enable Release QA, automated testing
            on pull requests, and code-aware features.
          </Text>
          <Flex gap="md" align="center" justify="center" className="mt-6">
            <Button
              variant="primary"
              onPress={() => navigate({ to: '/settings', search: { tab: 'github' } })}
            >
              <GithubLogo size={16} />
              Connect GitHub
            </Button>
          </Flex>
        </div>
      </div>
    );
  }

  // Connected with linked repos
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Flex align="center" justify="between">
            <div>
              <Heading level={3}>Linked Repositories</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                GitHub repositories linked to this application for automated testing
              </Text>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onPress={() => setIsLinkDialogOpen(true)}
              iconLeft={<Plus size={16} />}
              isDisabled={unlinkedRepos.length === 0}
            >
              Link Repository
            </Button>
          </Flex>
        </CardHeader>
        <CardContent>
          {linkedReposQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-8">
              <LoadingOrbit size="md" />
            </Flex>
          ) : linkedReposQuery.data?.items && linkedReposQuery.data.items.length > 0 ? (
            <div className="space-y-2">
              {linkedReposQuery.data.items.map((repo) => (
                <div
                  key={repo.id}
                  className="flex items-center justify-between py-3 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <GithubLogo size={20} className="text-muted-foreground" />
                    <div>
                      <a
                        href={`https://github.com/${repo.repoFullName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline"
                      >
                        {repo.repoFullName}
                      </a>
                      <Flex align="center" gap="sm" className="mt-1">
                        {repo.isPrivate && (
                          <TagBadge name="Private" color="gray" hideDot className="text-xs" />
                        )}
                        {repo.defaultBranch && (
                          <Flex align="center" gap="xs" className="text-xs text-muted-foreground">
                            <GitBranch size={12} />
                            {repo.defaultBranch}
                          </Flex>
                        )}
                        {repo.rootDir && repo.rootDir !== '/' && (
                          <TagBadge name={repo.rootDir} color="gray" hideDot className="text-xs font-mono" />
                        )}
                      </Flex>
                      {repo.agentHint && (
                        <Text size="xs" className="text-muted-foreground mt-1 line-clamp-1">
                          {repo.agentHint}
                        </Text>
                      )}
                    </div>
                  </div>
                  <Flex gap="xs">
                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={() => {
                        setEditingRepo(repo);
                        setEditRootDir(repo.rootDir ?? '/');
                        setEditAgentHint(repo.agentHint ?? '');
                        setEditPrReviewGuidance(repo.prReviewGuidance ?? '');
                        setIsEditDialogOpen(true);
                      }}
                    >
                      <PencilSimple size={16} className="text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={() => {
                        if (window.confirm(`Unlink ${repo.repoFullName}?`)) {
                          unlinkRepoMutation.mutate(repo.repoFullName);
                        }
                      }}
                      isDisabled={unlinkRepoMutation.isPending}
                    >
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </Flex>
                </div>
              ))}
            </div>
          ) : (
            <Flex direction="column" align="center" gap="md" className="py-8">
              <div className="p-3 bg-muted rounded-full">
                <LinkSimple size={24} className="text-muted-foreground" />
              </div>
              <Text size="sm" className="text-muted-foreground text-center">
                No repositories linked yet.
                {unlinkedRepos.length > 0
                  ? ' Click "Link Repository" to connect one.'
                  : ' Configure repository access in your GitHub App settings first.'}
              </Text>
              {unlinkedRepos.length > 0 && (
                <Button variant="secondary" size="sm" onPress={() => setIsLinkDialogOpen(true)}>
                  <Plus size={16} className="mr-1" />
                  Link Your First Repository
                </Button>
              )}
            </Flex>
          )}
        </CardContent>
      </Card>

      {releaseQaGithubChecksEnabled &&
        linkedReposQuery.data?.items &&
        linkedReposQuery.data.items.length > 0 && (
        <Card>
          <CardHeader>
            <Heading level={3}>Release QA GitHub Checks</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Post a GitHub Check on the head SHA of the latest open PR matching a branch
              glob whenever a release QA run starts/finishes. Leave the glob empty to disable
              posting for that repo. Tip: <code>release-please--branches--*</code> targets
              release-please PRs.
            </Text>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {linkedReposQuery.data.items.map((repo) => {
                const saved = repo.releaseCheckBranchGlob ?? '';
                const draft = globDrafts[repo.id] ?? saved;
                const dirty = draft.trim() !== saved.trim();
                const isPending =
                  updateRepoMutation.isPending && updateRepoMutation.variables?.repoId === repo.id;
                return (
                  <div
                    key={repo.id}
                    className="flex items-end gap-3 py-3 border-b border-border last:border-0"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-56">
                      <GithubLogo size={16} className="text-muted-foreground flex-shrink-0" />
                      <span className="font-medium text-sm truncate">{repo.repoFullName}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <TextField
                        aria-label={`Branch glob for ${repo.repoFullName}`}
                        value={draft}
                        onChange={(value) =>
                          setGlobDrafts((prev) => ({ ...prev, [repo.id]: value }))
                        }
                        placeholder="release-please--branches--*"
                      />
                    </div>
                    <Button
                      variant={dirty ? 'primary' : 'ghost'}
                      size="sm"
                      isDisabled={!dirty || isPending}
                      onPress={() =>
                        updateRepoMutation.mutate(
                          {
                            repoId: repo.id,
                            releaseCheckBranchGlob: draft.trim() || null,
                          },
                          {
                            onSuccess: () =>
                              setGlobDrafts((prev) => {
                                const next = { ...prev };
                                delete next[repo.id];
                                return next;
                              }),
                          }
                        )
                      }
                    >
                      {isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {linkedReposQuery.data?.items && linkedReposQuery.data.items.length > 0 && (
        <PropertyCodeRunbooksSection
          propertyId={propertyId}
          linkedRepos={linkedReposQuery.data.items}
        />
      )}

      {linkedReposQuery.data?.items && linkedReposQuery.data.items.length > 0 && (
        <PropertyCodeMonitorsSection
          propertyId={propertyId}
          linkedRepos={linkedReposQuery.data.items}
        />
      )}

      <PropertyCodeLinkRepositoryDialog
        isOpen={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        repositories={unlinkedRepos}
        isLoading={availableReposQuery.isLoading}
        selectedRepo={selectedRepo}
        onSelectRepo={setSelectedRepo}
        rootDir={linkRootDir}
        onRootDirChange={setLinkRootDir}
        agentHint={linkAgentHint}
        onAgentHintChange={setLinkAgentHint}
        isPending={linkRepoMutation.isPending}
        onCancel={() => {
          setIsLinkDialogOpen(false);
          setSelectedRepo(null);
          setLinkRootDir('/');
          setLinkAgentHint('');
        }}
        onLink={() =>
          selectedRepo &&
          linkRepoMutation.mutate({
            repoFullName: selectedRepo,
            rootDir: linkRootDir || '/',
            agentHint: linkAgentHint || undefined,
          })
        }
      />

      <PropertyCodeEditRepositoryDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        repository={editingRepo}
        rootDir={editRootDir}
        onRootDirChange={setEditRootDir}
        agentHint={editAgentHint}
        onAgentHintChange={setEditAgentHint}
        showPrReviewGuidance={prReviewEnabled}
        prReviewGuidance={editPrReviewGuidance}
        onPrReviewGuidanceChange={setEditPrReviewGuidance}
        isPending={updateRepoMutation.isPending}
        onCancel={() => {
          setIsEditDialogOpen(false);
          setEditingRepo(null);
        }}
        onSave={() =>
          editingRepo &&
          updateRepoMutation.mutate({
            repoId: editingRepo.id,
            rootDir: editRootDir || '/',
            agentHint: editAgentHint || null,
            prReviewGuidance: prReviewEnabled ? editPrReviewGuidance || null : undefined,
          })
        }
      />
    </div>
  );
}
